import {
  allocationsMatch,
  toChf,
  type AdminClientInvoice,
  type ClientPaymentAllocation,
  type ClientPaymentFxRules,
} from '@carsfinders/contracts';

/**
 * «Додати платіж» — the pure allocation rules of the dialog.
 *
 * Nothing here decides money on its own: the server re-runs the same checks
 * (`allocationsMatch`, `toChf`) inside the payment transaction. These exist so
 * the operator sees the answer BEFORE the round trip — submit stays disabled
 * until the parts add up, in whole cents, exactly as the server counts them.
 */

/** Where the money goes. `invoice` / `split` exist only while `PAYMENTS_ENABLED` is on. */
export type PaymentTarget = 'deposit' | 'invoice' | 'split';

/** Currencies the dialog offers — the rule set has only the EUR→CHF rate (owner, 2026-09-29). */
export type DialogCurrency = 'CHF' | 'EUR';

/** An invoice money can still be allocated to: it owes, and is neither paid nor cancelled. */
export function isPayable(
  invoice: Pick<AdminClientInvoice, 'outstandingChf' | 'displayStatus'>,
): boolean {
  return (
    invoice.outstandingChf > 0 &&
    invoice.displayStatus !== 'paid' &&
    invoice.displayStatus !== 'cancelled'
  );
}

/** "1 200", "1200.50", "1200,50" → number (`\s` also strips the NBSP of uk-UA grouping); anything else → NaN. */
export function parseAmount(raw: string): number {
  const normalized = raw.replace(/\s+/g, '').replace(',', '.');
  if (normalized === '' || !/^\d+(\.\d{1,2})?$/.test(normalized)) return Number.NaN;
  return Number(normalized);
}

/** A split part: an empty field is 0 (the part is simply not used), anything else as {@link parseAmount}. */
export function parsePart(raw: string): number {
  return raw.trim() === '' ? 0 : parseAmount(raw);
}

function roundCents(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The payment in CHF — the ≈ CHF preview and the figure every allocation is
 * counted against. The contracts `toChf`, so it is the server's number too.
 * Null when the amount is not a positive number or EUR has no usable rate.
 */
export function paymentAmountChf(
  amount: number,
  currency: DialogCurrency,
  fx: ClientPaymentFxRules | null,
): number | null {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (currency === 'EUR' && !fx) return null;
  const rules = fx ?? { chfToEurRate: 1, ratesVersion: '' };
  const converted = toChf(amount, currency, rules);
  return converted && converted.amountChf > 0 ? converted.amountChf : null;
}

/** Split pre-fill: clear the invoice debt first (≤ what it owes), the rest to the deposit. */
export function splitPrefill(
  amountChf: number,
  outstandingChf: number,
): { invoiceChf: number; depositChf: number } {
  const invoiceChf = roundCents(Math.max(0, Math.min(outstandingChf, amountChf)));
  return { invoiceChf, depositChf: roundCents(amountChf - invoiceChf) };
}

/** Why submit is disabled; null when the allocations may be sent. */
export type AllocationProblem = 'amount' | 'invoiceMissing' | 'overOutstanding' | 'mismatch';

export interface AllocationInput {
  /** {@link paymentAmountChf}; null = no valid amount yet. */
  amountChf: number | null;
  target: PaymentTarget;
  invoiceNo: string | null;
  /** The chosen invoice's outstanding CHF; null when none is chosen. */
  outstandingChf: number | null;
  /** Split only. */
  invoicePartChf: number;
  /** Split only. */
  depositPartChf: number;
}

export interface AllocationResult {
  allocations: ClientPaymentAllocation[];
  problem: AllocationProblem | null;
}

const refused = (problem: AllocationProblem): AllocationResult => ({ allocations: [], problem });

/** The allocations to send, or the reason there are none yet. */
export function evaluateAllocation(input: AllocationInput): AllocationResult {
  const { amountChf, target, invoiceNo, outstandingChf } = input;
  if (amountChf === null) return refused('amount');

  if (target === 'deposit') {
    return { allocations: [{ to: 'deposit', amountChf }], problem: null };
  }

  if (!invoiceNo || outstandingChf === null) return refused('invoiceMissing');

  if (target === 'invoice') {
    if (Math.round(amountChf * 100) > Math.round(outstandingChf * 100)) {
      return refused('overOutstanding');
    }
    return { allocations: [{ to: 'invoice', invoiceNo, amountChf }], problem: null };
  }

  const { invoicePartChf, depositPartChf } = input;
  if (
    !Number.isFinite(invoicePartChf) ||
    !Number.isFinite(depositPartChf) ||
    invoicePartChf < 0 ||
    depositPartChf < 0
  ) {
    return refused('mismatch');
  }
  if (Math.round(invoicePartChf * 100) > Math.round(outstandingChf * 100)) {
    return refused('overOutstanding');
  }
  const allocations: ClientPaymentAllocation[] = [];
  if (invoicePartChf > 0) allocations.push({ to: 'invoice', invoiceNo, amountChf: invoicePartChf });
  if (depositPartChf > 0) allocations.push({ to: 'deposit', amountChf: depositPartChf });
  if (allocations.length === 0 || !allocationsMatch(allocations, amountChf)) {
    return refused('mismatch');
  }
  return { allocations, problem: null };
}
